import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Country } from '../../../models/supplierAdministration';
import { UserDTO } from '../../../models/userDTO';
import { supplierAdministrationApi } from '../../../services/supplierAdministrationApi';
import AddressesTab from './SuppliersAdministration/AddressesTab';
import CountriesTab from './SuppliersAdministration/CountriesTab';
import { ADDRESS_ADMINISTRATION_MODIFY, hasAdministrationPermission } from './SuppliersAdministration/permissions';

type AddressAdministrationPartProps = {
	user: UserDTO;
}

type LocalTabPanelProps = {
	value: number;
	index: number;
	children: React.ReactNode;
}

const LocalTabPanel = ({ value, index, children }: LocalTabPanelProps) => {
	return (
		<Box role="tabpanel" hidden={value !== index} sx={{ mt: 2 }}>
			{value === index && children}
		</Box>
	);
};

const AddressAdministrationPart = ({ user }: AddressAdministrationPartProps) => {
	const [value, setValue] = useState(0);
	const [countries, setCountries] = useState<Country[]>([]);
	const [countriesLoading, setCountriesLoading] = useState(false);
	const [countriesError, setCountriesError] = useState<string | null>(null);

	const canModifyAddresses = useMemo(
		() => hasAdministrationPermission(user, ADDRESS_ADMINISTRATION_MODIFY),
		[user]
	);

	useEffect(() => {
		setCountriesLoading(true);
		setCountriesError(null);
		supplierAdministrationApi.listCountries()
			.then(setCountries)
			.catch((error: Error) => setCountriesError(error.message))
			.finally(() => setCountriesLoading(false));
	}, []);

	return (
		<Box sx={{ width: '100%' }}>
			<Typography variant="h4" color="primary" sx={{ mb: 2 }}>
				Administration des adresses
			</Typography>
			{!canModifyAddresses && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Vous disposez d'un accès en lecture seule sur cette administration.
				</Alert>
			)}
			<Tabs value={value} onChange={(_, newValue: number) => setValue(newValue)} variant="scrollable" scrollButtons="auto">
				<Tab label="Adresses" />
				<Tab label="Pays" />
			</Tabs>
			<LocalTabPanel value={value} index={0}>
				<AddressesTab canModify={canModifyAddresses} countries={countries} />
			</LocalTabPanel>
			<LocalTabPanel value={value} index={1}>
				<CountriesTab countries={countries} loading={countriesLoading} error={countriesError} />
			</LocalTabPanel>
			<ToastContainer />
		</Box>
	);
};

export default AddressAdministrationPart;
